import { apiClient } from "./apiClient";
import { BATCH_ENDPOINTS } from "./endpoints";

/**
 * One function per TraceSync Batch endpoint (see swagger:
 * /tracesync/swagger-docs#/Batch). This is the only module that should
 * ever call `apiClient` for batch data — pages/components call these
 * functions, never Axios directly.
 *
 * Every function returns the parsed response payload (`response.data`) or,
 * for the two download endpoints, the raw Blob. Errors are already
 * normalized to `{ status, message, data }` by apiClient's interceptor, so
 * callers can rely on one error shape everywhere.
 */
const requireSuccess = (response) => {
  if (response?.status === "failed") throw new Error(response.message || "The batch action failed.");
  return response;
};

export const batchApi = {
  start: (uid, payload) => apiClient.post(`batch/${encodeURIComponent(uid)}/start`, payload).then(res => requireSuccess(res.data)),
  rework: (uid, payload) => apiClient.post(`batch/${encodeURIComponent(uid)}/rework`, payload).then(res => requireSuccess(res.data)),
  complete: (uid, payload) => apiClient.post(`batch/${encodeURIComponent(uid)}/complete`, payload).then(res => requireSuccess(res.data)),
  getCycles: (uid, params = {}) => apiClient.get(`batch/${encodeURIComponent(uid)}/cycles`, { params }).then(res => res.data),
  getAllCycles: async (uid) => {
    const first = await batchApi.getCycles(uid, { page: 1, limit: 1000 });
    const rows = [...(first?.data || [])];
    const total = first?.meta_data?.total_records ?? rows.length;
    for (let page = 2; rows.length < total; page += 1) {
      const next = await batchApi.getCycles(uid, { page, limit: 1000 });
      if (!next?.data?.length) throw new Error("Cycle data changed while loading. Refresh and try again.");
      rows.push(...next.data);
    }
    return { ...first, data: rows };
  },
  updateCycle: (uid, cycleNo, payload) => apiClient.put(`batch/${encodeURIComponent(uid)}/cycles/${encodeURIComponent(cycleNo)}`, payload).then(res => requireSuccess(res.data)),
  /** GET /tracesync/batch/ — pagination/filter/search/sort params are
   * passed straight through as query params (page, limit, search, sort,
   * plus any batch-field filters the API accepts). */
  list: (params = {}) => apiClient.get(BATCH_ENDPOINTS.list, { params }).then((res) => res.data),

  /** GET /tracesync/batch/{uid} */
  getByUid: (uid) => apiClient.get(BATCH_ENDPOINTS.detail(uid)).then((res) => requireSuccess(res.data)),

  /** POST /tracesync/batch/ */
  create: (payload) => apiClient.post(BATCH_ENDPOINTS.create, payload).then((res) => requireSuccess(res.data)),

  /** PUT /tracesync/batch/{uid} */
  update: (uid, payload) => apiClient.put(BATCH_ENDPOINTS.update(uid), payload).then((res) => requireSuccess(res.data)),

  /** DELETE /tracesync/batch/{uid} */
  remove: (uid) => apiClient.delete(BATCH_ENDPOINTS.delete(uid)).then((res) => requireSuccess(res.data)),

  /** GET /tracesync/batch/dispatch-history */
  getDispatchHistory: (params = {}) =>
    apiClient.get(BATCH_ENDPOINTS.dispatchHistory, { params }).then((res) => res.data),

  /** POST /tracesync/batch/dispatch */
  dispatch: (payload) => apiClient.post(BATCH_ENDPOINTS.dispatch, payload).then((res) => requireSuccess(res.data)),

  /** GET /tracesync/batch/{uid}/dispatch-slip — binary response (PDF). */
  downloadDispatchSlip: (uid) =>
    apiClient.get(BATCH_ENDPOINTS.dispatchSlip(uid), { responseType: "blob" }).then((res) => requireSuccess(res.data)),

  /** POST /tracesync/batch/qr/download — binary response (ZIP of QR codes). */
  downloadQrZip: (payload) =>
    apiClient
      .post(BATCH_ENDPOINTS.qrDownload, payload, { responseType: "blob" })
      .then((res) => requireSuccess(res.data)),
};

export default batchApi;
